interface CacheItem<T> {
  data: T;
  timestamp: number;
  ttl: number;
}

class Cache {
  private storage: Map<string, CacheItem<any>> = new Map();

  /**
   * Set a value in cache with TTL (time to live) in milliseconds
   */
  set<T>(key: string, value: T, ttl: number): void {
    const item: CacheItem<T> = {
      data: value,
      timestamp: Date.now(),
      ttl
    };
    this.storage.set(key, item);
  }

  /**
   * Get a value from cache, returns null if expired or not found
   */
  get<T>(key: string): T | null {
    const item = this.storage.get(key);
    
    if (!item) {
      return null;
    }

    const now = Date.now();
    const isExpired = now - item.timestamp > item.ttl;

    if (isExpired) {
      this.storage.delete(key);
      return null;
    }

    return item.data as T;
  }

  /**
   * Remove a specific key from cache
   */
  delete(key: string): boolean {
    return this.storage.delete(key);
  }

  /**
   * Clear all cache entries
   */
  clear(): void {
    this.storage.clear();
  }

  /**
   * Clean up expired entries
   */
  cleanup(): void {
    const now = Date.now();
    for (const [key, item] of this.storage.entries()) {
      const isExpired = now - item.timestamp > item.ttl;
      if (isExpired) {
        this.storage.delete(key);
      }
    }
  }

  /**
   * Get cache statistics
   */
  getStats(): { size: number; keys: string[] } {
    return {
      size: this.storage.size,
      keys: Array.from(this.storage.keys())
    };
  }
}

// Export a singleton instance
export const cache = new Cache();

// Optional: Set up periodic cleanup (every 10 minutes)
if (typeof window !== 'undefined') {
  setInterval(() => {
    cache.cleanup();
  }, 10 * 60 * 1000);
}